import { useId, useRef, useState } from 'react'
import { X } from 'lucide-react'

const EMAIL_RE = /^[^\s@,;]+@[^\s@,;]+\.[^\s@,;]+$/

/**
 * Module-level state shared across all AddressField instances during a drag.
 * Avoids racing dragend against drop and lets the target atomically move the
 * chip from its source without going through text serialization.
 */
let inflight: {
  address: string
  sourceFieldId: string
  removeFromSource: () => void
  landed: boolean
} | null = null

export interface AddressFieldProps {
  label: string
  addresses: string[]
  onChange: (next: string[]) => void
  onRemoveField?: () => void
  autoFocus?: boolean
}

export default function AddressField({
  label, addresses, onChange, onRemoveField, autoFocus,
}: AddressFieldProps) {
  const fieldId = useId()
  const inputRef = useRef<HTMLInputElement>(null)
  const [text, setText] = useState('')
  const [dragOver, setDragOver] = useState(false)

  const commit = (raw: string) => {
    const parts = raw.split(/[\s,;]+/).map(s => s.trim()).filter(Boolean)
    if (!parts.length) return
    const next = [...addresses]
    for (const p of parts) if (!next.includes(p)) next.push(p)
    onChange(next)
    setText('')
  }

  const addOne = (addr: string) => {
    if (addresses.includes(addr)) return
    onChange([...addresses, addr])
  }

  const remove = (addr: string) => onChange(addresses.filter(a => a !== addr))

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDragOver(false)
    if (inflight) {
      inflight.landed = true
      if (inflight.sourceFieldId !== fieldId) {
        addOne(inflight.address)
        inflight.removeFromSource()
      }
      inflight = null
      return
    }
    // Fallback: external drop (paste-style)
    const raw = e.dataTransfer.getData('text/plain')
    if (raw) commit(raw)
  }

  const stopDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }

  return (
    <div
      className={`flex items-baseline gap-3 py-1 border-b transition-colors
                  ${dragOver ? 'border-pulse bg-pulse/5' : 'border-line'}`}
      onDragOver={e => {
        if (inflight) e.dataTransfer.dropEffect = 'move'
        e.preventDefault()
        setDragOver(true)
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={handleDrop}
    >
      <label className="h-eyebrow w-14 flex-shrink-0">{label}</label>
      <div
        className="flex-1 flex flex-wrap gap-1.5 items-center min-h-[28px]"
        onClick={() => inputRef.current?.focus()}
      >
        {addresses.map(a => (
          <Chip key={a}
                address={a}
                sourceFieldId={fieldId}
                onRemoveFromSource={() => remove(a)} />
        ))}
        <input
          ref={inputRef}
          value={text}
          autoFocus={autoFocus}
          onChange={e => {
            const v = e.target.value
            if (/[,;\n]/.test(v)) commit(v.replace(/[,;\n]/g, ' '))
            else setText(v)
          }}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === 'Tab' || e.key === ' ') {
              if (text.trim()) { e.preventDefault(); commit(text) }
            } else if (e.key === 'Backspace' && !text && addresses.length) {
              onChange(addresses.slice(0, -1))
            }
          }}
          onBlur={() => text.trim() && commit(text)}
          onPaste={e => {
            const p = e.clipboardData.getData('text')
            if (/[\s,;]/.test(p)) { e.preventDefault(); commit(p) }
          }}
          onDragOver={stopDrop}
          onDrop={handleDrop}
          placeholder={addresses.length ? '' : 'name@example.com'}
          className="flex-1 min-w-[140px] bg-transparent border-none outline-none text-sm text-ink-900 placeholder:text-ink-300"
        />
      </div>
      {onRemoveField && (
        <button onClick={onRemoveField} className="text-ink-300 hover:text-ink-900 pt-2" title="Remove field">
          <X size={12} />
        </button>
      )}
    </div>
  )
}

function Chip({ address, sourceFieldId, onRemoveFromSource }: {
  address: string
  sourceFieldId: string
  onRemoveFromSource: () => void
}) {
  const valid = EMAIL_RE.test(address)
  return (
    <span
      draggable
      onDragStart={e => {
        inflight = { address, sourceFieldId, removeFromSource: onRemoveFromSource, landed: false }
        e.dataTransfer.setData('text/plain', address)
        e.dataTransfer.effectAllowed = 'move'
      }}
      onDragEnd={() => {
        // Guarantee the chip is preserved if it wasn't dropped into an AddressField.
        // (No mutations here — moves are handled atomically in the target's onDrop.)
        inflight = null
      }}
      className={`inline-flex items-center gap-1.5 rounded-pill pl-2.5 pr-1 py-0.5 text-xs cursor-grab active:cursor-grabbing select-none
                  ${valid ? 'bg-canvas text-ink-900' : 'bg-red-50 text-red-800 ring-1 ring-red-200'}`}
      title={valid ? address : 'Invalid email format'}
    >
      <span>{address}</span>
      <button
        onClick={e => { e.stopPropagation(); onRemoveFromSource() }}
        onMouseDown={e => e.stopPropagation()}
        className="rounded-full hover:bg-ink-900/10 p-0.5"
        aria-label={`Remove ${address}`}
      >
        <X size={11} />
      </button>
    </span>
  )
}
